import {
  FVS_STATUS,
  FVS_COMMENT_STATUS,
  FVS_COMMENT_AUDIENCE,
} from './constants';

/**
 * Status tags for Federal Vehicle Standards, shared by the authenticated and
 * public FVS pages.
 *
 * The two page trees are otherwise duplicated, but this logic is identical in
 * both and is the single source of truth for how a standard's status is
 * labelled - including the retired composite values that only survive so that
 * historical status-history rows keep rendering.
 */

export const mapStatusToTags = (status) => {
  switch (status) {
    case FVS_STATUS.Draft: {
      return [
        {
          value: 'Draft',
          status,
          variant: 'Info-Gray',
        },
      ];
    }
    // AFP-229609 removed peer review from the workflow, but the status is still
    // reachable here: the service reserves the enum value so that historical
    // fvs_status_history rows keep serializing, and fvs-history renders them
    // through this same map. No standard can enter this status any more.
    case FVS_STATUS.PeerReview: {
      return [
        {
          value: 'Peer review',
          status,
          variant: 'Warning-Gray',
        },
      ];
    }
    case FVS_STATUS.PSOCommentOpen: {
      return [
        {
          value: 'Vehicle Supplier comment open',
          status,
          variant: 'Warning-Gray',
        },
      ];
    }
    case FVS_STATUS.PublicCommentOpen: {
      return [
        {
          value: 'Public comment open',
          status,
          variant: 'Warning-Gray',
        },
      ];
    }
    case FVS_STATUS.AllCommentOpen: {
      return [
        {
          value: 'Vehicle Supplier comment open',
          status,
          variant: 'Warning-Gray',
        },
        {
          value: 'Public comment open',
          status,
          variant: 'Warning-Gray',
        },
      ];
    }
    case FVS_STATUS.AllCommentClosed: {
      return [
        {
          value: 'Vehicle Supplier comment closed',
          status,
          variant: 'Inactive-Gray',
        },
        {
          value: 'Public comment closed',
          status,
          variant: 'Inactive-Gray',
        },
      ];
    }
    case FVS_STATUS.Published: {
      return [
        {
          value: 'Published',
          status,
          variant: 'Ready-Gray',
        },
      ];
    }
    case FVS_STATUS.Amended: {
      return [
        {
          value: 'Amended',
          status,
          variant: 'Ready-Gray',
        },
      ];
    }
    default:
      return [];
  }
};

// Switches rather than objects keyed by the imported constants: these are
// evaluated lazily, so a test that mocks utilities/constants with only some of
// the keys can still import this module.
const audienceLabel = (audience) =>
  audience === FVS_COMMENT_AUDIENCE.VehicleSupplier
    ? 'Vehicle Supplier'
    : 'Public';

const commentStatusTag = (status) => {
  switch (status) {
    case FVS_COMMENT_STATUS.Scheduled:
      return { suffix: 'comment period scheduled', variant: 'Info-Gray' };
    case FVS_COMMENT_STATUS.Open:
      return { suffix: 'comment open', variant: 'Warning-Gray' };
    case FVS_COMMENT_STATUS.Closed:
      return { suffix: 'comment closed', variant: 'Inactive-Gray' };
    default:
      return null;
  }
};

/** One tag for an audience, or nothing when that audience has no periods. */
const audienceTag = (audience, status) => {
  const tag = commentStatusTag(status);
  if (!tag) return [];
  return [
    {
      value: `${audienceLabel(audience)} ${tag.suffix}`,
      status,
      variant: tag.variant,
    },
  ];
};

/**
 * Tags for the Status line on the FVS detail panel (AFP-229609 PR 2).
 *
 * Commenting state lives per audience, so a standard can show two tags in
 * different states. Published and Amended are terminal and replace the
 * commenting tags rather than appearing beside them; a standard with no
 * periods at all reads Draft.
 */
export const mapFvsStatusToTags = (fvs) => {
  if (
    fvs?.status === FVS_STATUS.Published ||
    fvs?.status === FVS_STATUS.Amended
  ) {
    return mapStatusToTags(fvs.status);
  }

  const tags = [
    ...audienceTag(FVS_COMMENT_AUDIENCE.VehicleSupplier, fvs?.vsCommentStatus),
    ...audienceTag(FVS_COMMENT_AUDIENCE.Public, fvs?.publicCommentStatus),
  ];

  return tags.length ? tags : mapStatusToTags(FVS_STATUS.Draft);
};

/**
 * Tags for one row of the status history.
 *
 * Rows written since PR 2 record a single audience's move and carry userType +
 * commentStatus. Older rows carry a composite status (including the retired
 * Peer review one) and still render through mapStatusToTags.
 */
export const mapHistoryRowToTags = (row) => {
  if (row?.userType) {
    return audienceTag(row.userType, row.commentStatus);
  }
  return mapStatusToTags(row?.status);
};

export default mapStatusToTags;
